import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Animated,
  Easing,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { connect } from "react-redux";
import { MaterialIcons as Icon, MaterialCommunityIcons as Icon2 } from "@expo/vector-icons";
import { BRANDING } from "@env";
import { setIsLogGroup, setLogGroupSelected } from "../../actions/loggroups";
import { setFormsFromAPI } from "../../actions/tasks";
import { simpleAlert } from "../../utils";
import colors from "../../constants/colors";

const HIDDEN_SCREENS = [
  "Login",
  "Forgot",
  "Register",
  "FormView",
  "GroupView",
  "SubmenuView",
  "PromptFormView"
];

const ROUTE_TAB_MAP = {
  MainView: "dashboard",
  RecordsView: "records",
  PromptFormView: "forms",
};

const LABEL_VISIBLE_MS = 1000;

const TAB_ICONS = {
  dashboard: {
    selected: require("../../../assets/Icons-Screens/dashboard-selected.png"),
    unselected: require("../../../assets/Icons-Screens/dashboard-unselected.png"),
  },
  inventory: {
    selected: require("../../../assets/Icons-Screens/inventory-selected.png"),
    unselected: require("../../../assets/Icons-Screens/inventory-unselected.png"),
  },
  production: {
    selected: require("../../../assets/Icons-Screens/production-selected.png"),
    unselected: require("../../../assets/Icons-Screens/production-unselected.png"),
  },
  records: {
    selected: require("../../../assets/Icons-Screens/records-selected.png"),
    unselected: require("../../../assets/Icons-Screens/records-unselected.png"),
  },
};

function BottomNav({
  navigationRef,
  authToken,
  userRole,
  isOnline,
  currentRoute,
  formsFilter,
  setIsLogGroup,
  setLogGroupSelected,
  setFormsFromAPI,
}) {
  const insets = useSafeAreaInsets();
  const screen = currentRoute;
  const labelsProgress = useRef(new Animated.Value(1)).current;
  const hideTimer = useRef(null);
  const labelsFor = useRef(null);
  const navVisible =
    BRANDING === "iqops" &&
    !!authToken &&
    !!screen &&
    !HIDDEN_SCREENS.includes(screen);
  const menuShift = useRef(new Animated.Value(navVisible ? 0 : 1)).current;
  const [menuMounted, setMenuMounted] = useState(navVisible);

  const showLabelsBriefly = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    Animated.timing(labelsProgress, {
      toValue: 1,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
    hideTimer.current = setTimeout(() => {
      Animated.timing(labelsProgress, {
        toValue: 0,
        duration: 320,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }, LABEL_VISIBLE_MS);
  }, [labelsProgress]);

  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (BRANDING !== "iqops" || !authToken || !screen) {
      menuShift.setValue(1);
      setMenuMounted(false);
      return undefined;
    }
    if (navVisible) {
      setMenuMounted(true);
      const frame = requestAnimationFrame(() => {
        if (cancelled) return;
        Animated.timing(menuShift, {
          toValue: 0,
          duration: 320,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }).start();
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
      };
    }
    Animated.timing(menuShift, {
      toValue: 1,
      duration: 280,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished && !cancelled) setMenuMounted(false);
    });
    return () => {
      cancelled = true;
    };
  }, [navVisible, authToken, screen, menuShift]);

  const activeTab = useMemo(() => {
    if (screen === "NewTask" || screen === "NewLogTask") {
      if (formsFilter === "inventoryForms") return "inventory";
      if (formsFilter === "productionForms") return "production";
      return "forms";
    }
    return ROUTE_TAB_MAP[screen] || null;
  }, [formsFilter, screen]);

  const revealLabels = useCallback((key) => {
    if (!key || labelsFor.current === key) return;
    labelsFor.current = key;
    showLabelsBriefly();
  }, [showLabelsBriefly]);

  useEffect(() => {
    if (!authToken) {
      labelsFor.current = null;
      return;
    }
    if (!activeTab || !navVisible) return;
    revealLabels(activeTab);
  }, [activeTab, authToken, navVisible, revealLabels]);

  const iconScale = labelsProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [1.22, 1],
  });

  const showInternetWarning = useCallback(() => {
    simpleAlert(
      "Warning",
      "This functionality requires internet, please reconnect and try again.",
      "OK"
    );
  }, []);

  const navigateTo = useCallback(
    (routeName, beforeNavigate) => {
      const nav = navigationRef?.current;
      if (!nav) return;

      if (!isOnline && (routeName === "NewTask" || routeName === "RecordsView")) {
        showInternetWarning();
        return;
      }
      beforeNavigate?.();
      if (screen !== routeName) {
        nav.navigate(routeName);
      }
    },
    [isOnline, navigationRef, screen, showInternetWarning]
  );

  const openFormsMenu = useCallback(
    (filter) => {
      navigateTo("NewTask", () => {
        setIsLogGroup(false);
        setFormsFromAPI(filter);
      });
    },
    [navigateTo, setFormsFromAPI, setIsLogGroup]
  );

  const tabs = useMemo(
    () =>
      [
        {
          key: "dashboard",
          label: "Dashboard",
          onPress: () => navigateTo("MainView"),
        },
        {
          key: "forms",
          label: "Forms",
          hidden: userRole === "Auditor",
          onPress: () => openFormsMenu(null),
        },
        {
          key: "inventory",
          label: "Inventory",
          onPress: () => openFormsMenu("inventoryForms"),
        },
        {
          key: "production",
          label: "Production",
          onPress: () => openFormsMenu("productionForms"),
        },
        {
          key: "records",
          label: "Records",
          onPress: () =>
            navigateTo("RecordsView", () => {
              setLogGroupSelected({});
              setIsLogGroup(false);
            }),
        },
      ].filter((tab) => !tab.hidden),
    [navigateTo, openFormsMenu, setIsLogGroup, setLogGroupSelected, userRole]
  );

  const bottomOffset = Math.max(insets.bottom, 8);
  const translateY = menuShift.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 200],
  });

  if (BRANDING !== "iqops" || !authToken || !screen || !menuMounted) {
    return null;
  }

  return (
    <Animated.View
      pointerEvents={navVisible ? "box-none" : "none"}
      style={[
        styles.wrapper,
        { paddingBottom: bottomOffset, transform: [{ translateY }] },
      ]}
    >
      <View style={styles.shadowWrap}>
        <View style={[styles.pill, styles.pillGradient]}>
          <View style={styles.row}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <TouchableOpacity
                  key={tab.key}
                  style={styles.tab}
                  activeOpacity={0.75}
                  onPress={() => {
                    revealLabels(tab.key);
                    tab.onPress();
                  }}
                >
                  <View style={[styles.item, isActive && styles.itemActive]}>
                    <Animated.View style={{ transform: [{ scale: iconScale }] }}>
                      {tab.key === "forms" ? 
                        isActive ? <Icon name="description" size={27} color="#fff" /> : <Icon2 name="file-document-outline" size={27} color="#fff" />
                      : (
                        <Image
                          source={
                            isActive
                              ? TAB_ICONS[tab.key].selected
                              : TAB_ICONS[tab.key].unselected
                          }
                          style={[styles.icon, { tintColor: "#fff" }]}
                          resizeMode="contain"
                        />
                      )}
                    </Animated.View>
                    <Animated.Text
                      style={[
                        styles.label,
                        isActive && styles.labelActive,
                        { opacity: labelsProgress },
                      ]}
                      numberOfLines={1}
                    >
                      {tab.label}
                    </Animated.Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: Platform.OS === "web" ? "fixed" : "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    alignItems: "center",
    paddingHorizontal: 16,
  },
  shadowWrap: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 36,
    shadowColor: "#0b4dad",
    shadowOpacity: 0.22,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
  },
  pill: {
    borderRadius: 32,
    overflow: "hidden",
    //minHeight: 72,
    justifyContent: "center",
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.45)",
  },
  pillGradient: {
    backgroundColor: "transparent",
    backgroundImage: `linear-gradient(0deg, ${colors.iqopsMenuGlassLight} 0%, ${colors.iqopsMenuGlassMid} 50%, ${colors.iqopsMenuGlassEnd} 100%)`,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    //paddingHorizontal: 8,
    //paddingVertical: 8,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  item: {
    width: 96,
    height: 64,
    borderRadius: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  itemActive: {
    backgroundColor: colors.iqopsMenuSelectedCircle,
  },
  icon: {
    width: 27,
    height: 27,
  },
  label: {
    position: "absolute",
    bottom: 7,
    fontFamily: "Poppins-Medium",
    color: "#fff",
    fontSize: 11,
    fontWeight: "500",
    textAlign: "center",
    lineHeight: 14,
    width: 84,
  },
  labelActive: {
    fontFamily: "Poppins-ExtraBold",
    fontWeight: "800",
  },
});

export default connect(
  (state) => ({
    authToken: state.user.get("authToken"),
    userRole: state.user.get("userRole"),
    isOnline: true,
    currentRoute: state.user.get("currentRoute"),
    formsFilter: state.tasks.get("formsFilter"),
  }),
  (dispatch) => ({
    setIsLogGroup: (value) => dispatch(setIsLogGroup(value)),
    setLogGroupSelected: (value) => dispatch(setLogGroupSelected(value)),
    setFormsFromAPI: (value) => dispatch(setFormsFromAPI(value)),
  })
)(BottomNav);
